import type {ReactElement} from 'react';
import React, {useCallback, useEffect, useState} from 'react';
import {useLatest} from 'ahooks';
import styles from './ai-prompt-box-attachments.module.scss';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import type {AiPromptBoxAttachmentsProps, PromptAttachment} from './ai-prompt-box-attachments.types';
import AiPromptBoxAttachmentPreview from '@Components/ai-prompt-box/components/ai-prompt-box-attachment-preview/ai-prompt-box-attachment-preview';
import {AiPromptBoxLoadingAttachmentPreview} from '@Components/ai-prompt-box/components/ai-prompt-box-loading-attachment-preview';
import {ZoomableLightbox} from '@Components/zoomable-lightbox/zoomable-lightbox';
import {
  convertPromptAttachmentsToImageData,
  getAttachmentPreviewUrl,
  getAttachmentUniqueKey,
  LOADING_ATTACHMENT_CANCELLED_EVENT,
} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';

export function AiPromptBoxAttachments(props: AiPromptBoxAttachmentsProps): ReactElement | null {
  const {state, actions} = useAiPromptBox(props.id);
  const attachments = state.attachments?.items ?? [];

  const [isScrolling, setIsScrolling] = useState(false);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const stateRef = useLatest(state);

  useEffect(() => {
    if (!containerEl) return;
    let hideTimer: number | undefined;

    const handleScroll = (): void => {
      setIsScrolling(true);
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => {
        setIsScrolling(false);
      }, 400);
    };

    containerEl.addEventListener('scroll', handleScroll);
    return (): void => {
      containerEl.removeEventListener('scroll', handleScroll);
      window.clearTimeout(hideTimer);
    };
  }, [containerEl]);

  const handleRemoveAttachment = useCallback((index: number): void => {
    const currentState = stateRef.current;
    const updatedItems = (currentState.attachments?.items ?? []).filter((_, i) => i !== index);
    actions.update({
      attachments: {
        items: updatedItems,
        dropdown: currentState.attachments?.dropdown ?? [],
      },
      attachmentLoaders: currentState.attachmentLoaders ?? [],
    });
  }, [actions]);

  const handleRemoveLoadingAttachment = (id: string): void => {
    actions.removeLoadingAttachments([id]);
    window.dispatchEvent(new CustomEvent(LOADING_ATTACHMENT_CANCELLED_EVENT, {detail: {id}}));
  };

  const handleOpenLightbox = useCallback((index: number): void => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  }, []);

  const handleCloseLightbox = (): void => {
    setIsLightboxOpen(false);
  };

  const handlePreviousImage = (): void => {
    setCurrentImageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextImage = (): void => {
    setCurrentImageIndex((prev) => Math.min(attachments.length - 1, prev + 1));
  };

  const getAttachmentPreview = (attachment: PromptAttachment, index: number): ReactElement => {
    const previewUrl = getAttachmentPreviewUrl(attachment);
    const uniqueKey = getAttachmentUniqueKey(attachment);

    return (
      <AiPromptBoxAttachmentPreview key={uniqueKey} previewUrl={previewUrl} index={index} onRemove={handleRemoveAttachment} onPreviewClick={handleOpenLightbox} size={state.size} />
    );
  };

  const getLightboxHeader = (): ReactElement => {
    return (
      <div className={styles.lightboxHeader}>
        <Icon icon="icon-arrow-left" size={IconSize.SIZE_ICON_16} type={IconType.DARK_GHOST} shape={IconShape.SQUARE} onClick={handleCloseLightbox} />
      </div>
    );
  };

  const getAttachments = (): ReactElement | null => {
    const loadingAttachments = state.attachmentLoaders ?? [];

    if (attachments.length === 0 && loadingAttachments.length === 0) {
      return null;
    }

    return (
      <>
        <div ref={setContainerEl} className={`${styles.attachments} ${isScrolling ? styles.isScrolling : ''}`}>
          {loadingAttachments.map((loadingAttachment) => (
            <AiPromptBoxLoadingAttachmentPreview key={loadingAttachment.id} id={loadingAttachment.id} size={state.size} onRemove={handleRemoveLoadingAttachment} />
          ))}
          {attachments.map((attachment, index) => getAttachmentPreview(attachment, index))}
        </div>
        <ZoomableLightbox
          isOpen={isLightboxOpen}
          onClose={handleCloseLightbox}
          imagesAttachmentData={convertPromptAttachmentsToImageData(attachments, false)}
          currentImageIndex={currentImageIndex}
          onPreviousImage={handlePreviousImage}
          onNextImage={handleNextImage}
          headerContent={getLightboxHeader()}
        />
      </>
    );
  };

  return getAttachments();
}
